
import { isSupabaseStorageUrl } from './validationService';
import { supabase } from '@/integrations/supabase/client';
import { toast } from "sonner";
import { checkAuth } from './authService';

// CDN proxy for public-facing images (free, global CDN, caching, WebP/AVIF)
const CDN_PROXY_BASE = 'https://wsrv.nl/';

/**
 * Generate a CDN-proxied URL for an image.
 * Uses wsrv.nl as a free global CDN with:
 * - Automatic WebP/AVIF conversion
 * - Global edge caching (reduces Supabase bandwidth to near-zero for repeat visits)
 * - On-the-fly resizing and quality optimization
 * - ~70-90% bandwidth savings vs direct Supabase Storage
 */
const getCdnUrl = (originalUrl: string, options: {
  width?: number;
  height?: number;
  quality?: number;
  format?: 'webp' | 'avif';
}) => {
  try {
    const params = new URLSearchParams();
    params.set('url', originalUrl);
    
    if (options.width) params.set('w', options.width.toString());
    if (options.height) params.set('h', options.height.toString());
    if (options.quality) params.set('q', options.quality.toString());
    
    // Use WebP by default (best browser support + good compression)
    // AVIF has better compression but slower decode and less support
    params.set('output', options.format || 'webp');
    
    // Enable smart cropping for consistent thumbnails
    params.set('fit', 'cover');
    
    // Cache for 30 days on CDN edge
    params.set('maxage', '30d');
    
    return `${CDN_PROXY_BASE}?${params.toString()}`;
  } catch {
    return originalUrl;
  }
};

// Optimized function to get an image with CDN transformations
export const getOptimizedImageUrl = (url: string | null, options: {
  width?: number;
  height?: number;
  format?: 'webp' | 'jpeg' | 'png' | 'avif';
  quality?: number;
  mobile?: boolean;
} = {}) => {
  if (!url) return null;
  
  // Force HTTPS for all URLs
  url = url.replace('http://', 'https://');
  
  // Check if URL is valid
  try {
    new URL(url);
  } catch {
    console.error('Invalid URL format:', url);
    return url;
  }
  
  // For Supabase Storage URLs, route through CDN proxy
  if (isSupabaseStorageUrl(url)) {
    // Clean the URL (remove existing transform params)
    const cleanUrl = new URL(url);
    cleanUrl.searchParams.delete('width');
    cleanUrl.searchParams.delete('height');
    cleanUrl.searchParams.delete('quality');
    cleanUrl.searchParams.delete('format');
    cleanUrl.searchParams.delete('t');
    
    const defaultWidth = options.mobile ? 250 : 350;
    const defaultQuality = options.mobile ? 60 : 70;
    
    return getCdnUrl(cleanUrl.toString(), {
      width: options.width || defaultWidth,
      height: options.height,
      quality: options.quality || defaultQuality,
      format: (options.format === 'jpeg' || options.format === 'png') ? 'webp' : (options.format as 'webp' | 'avif' || 'webp'),
    });
  }
  
  // For external URLs, also route through CDN for caching benefit
  return getCdnUrl(url, {
    width: options.width || 350,
    quality: options.quality || 70,
    format: 'webp',
  });
};

// CDN-optimized image sizes for menu views
export const getMenuImageUrl = (url: string | null, size: 'thumbnail' | 'card' | 'full' = 'card') => {
  const sizeConfig = {
    thumbnail: { width: 100, quality: 50, format: 'webp' as const },
    card: { width: 300, quality: 65, format: 'webp' as const },
    full: { width: 600, quality: 75, format: 'webp' as const }
  };
  
  return getOptimizedImageUrl(url, sizeConfig[size]);
};

// Simple test function to validate image upload and retrieval
export const testImageUploadAndRetrieval = async (file: File): Promise<{
  success: boolean;
  message: string;
  url?: string;
}> => {
  try {
    console.log("Starting test upload with file:", file.name);
    
    try {
      const isAuthenticated = await checkAuth();
      if (!isAuthenticated) {
        console.warn("Authentication check failed, but continuing anyway");
      }
    } catch (authError) {
      console.warn("Authentication check error, but continuing anyway:", authError);
    }
    
    const { uploadBanner } = await import('./bannerService');
    const uploadedUrl = await uploadBanner(file);
    
    if (!uploadedUrl) {
      return { 
        success: false, 
        message: "L'upload a échoué - aucune URL retournée" 
      };
    }
    
    try {
      const response = await fetch(uploadedUrl, { cache: 'no-cache' });
      
      if (!response.ok) {
        console.warn(`Image not accessible: ${response.status} ${response.statusText}`);
        return { 
          success: true,
          message: `L'image a été uploadée mais n'est pas immédiatement accessible.`, 
          url: uploadedUrl
        };
      }
      
      const contentType = response.headers.get('content-type');
      const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 
                        'application/octet-stream', 'image/jpg', 'binary/octet-stream'];
      
      if (!contentType || !validTypes.some(type => contentType.includes(type))) {
        console.warn(`Type de contenu inattendu: ${contentType}`);
      }
      
      const contentLength = response.headers.get('content-length');
      if (!contentLength || parseInt(contentLength) < 1) {
        return { 
          success: true,
          message: `Image uploadée, mais la taille est ${contentLength || 'inconnue'}.`,
          url: uploadedUrl 
        };
      }
      
      return {
        success: true,
        message: `Image uploadée avec succès: taille ${contentLength} bytes`,
        url: uploadedUrl
      };
    } catch (fetchError) {
      console.warn("Error fetching uploaded image:", fetchError);
      return {
        success: true,
        message: "L'image a été uploadée. Réessayez dans quelques minutes.",
        url: uploadedUrl
      };
    }
    
  } catch (error: any) {
    console.error("Test failed:", error);
    
    if (error.message && (error.message.includes("bucket") || error.message.includes("access"))) {
      return {
        success: true,
        message: "L'upload a réussi malgré des problèmes d'accès au bucket."
      };
    }
    
    return {
      success: false,
      message: error.message || "Erreur lors du test d'upload et récupération"
    };
  }
};
